import { usePrevious } from '@ardoq/hooks';
import { isEqual } from 'lodash';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { DndContextProvider } from '../dndUtils/dndContext';
import { QueryBuilderFilterDefinitionGroup } from '../filterUtils';
import { useQueryBuilderReducer } from '../stateUtils/useQueryBuilderReducer';
import {
  ArdoqId,
  QueryBuilderQuery,
  QueryBuilderSubquery,
} from '@ardoq/api-types';
import {
  getIsEmpty,
  getIsReducerStateValid,
  reducerStateToInternalQuery,
  reducerStateToQuery,
} from '../stateUtils/utils';
import {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
  GetRuleNotificationParams,
  SearchContext,
} from '../types';
import { QueryBuilderWrapper } from './atoms';
import { QueryRow } from './QueryRow';
import {
  AiFeedbackWithDisclaimer,
  AiFeedbackWithDisclaimerProps,
} from '@ardoq/ai';
import { Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';

export type QueryBuilderProps = {
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
  locale: Locale;
  /**
   * Workspace ids that are available for Suggestions loading.
   * If provided, the suggestions will be loaded only from these workspaces data.
   */
  contextWorkspaceIds?: ArdoqId[];
  query?: QueryBuilderQuery;
  onQueryChange?: (query: QueryBuilderQuery) => void;
  onChange?: (args: {
    isValid: boolean;
    isEmpty: boolean;
    query: QueryBuilderQuery;
    subquery: QueryBuilderSubquery;
  }) => void;
  showErrors?: boolean;
  includeContextSelect?: boolean;
  hideContainerStyles?: boolean;
  hideTopLevelConditionBar?: boolean;
  maxSubqueryNestingLevel?: number;
  defaultSearchContext?: SearchContext;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  contextSelectIsReadOnly?: boolean;
  // for ReportBuilder we want to restrict queries to Component and Reference
  allowedSearchContexts?: SearchContext[];
  getFilterGroups: (args: {
    searchContext: SearchContext;
  }) => QueryBuilderFilterDefinitionGroup[];
  clickIdPrefix?: string;
  topLevelLabelContent?: ReactNode;
  dataTestId?: string;
  holdInvalidRules?: boolean;
  getRuleNotificationParams?: GetRuleNotificationParams;
  isCompact?: boolean;
  isLoading?: boolean;
  aiGenerateQueryFeedback?: AiFeedbackWithDisclaimerProps;
  i18nLocale?: I18nLocale;
  menuPortalTarget?: HTMLElement | null;
};

const extractSearchContextAndSubqueryFromQuery = (
  query?: QueryBuilderQuery
): {
  searchContext?: SearchContext;
  subquery?: QueryBuilderSubquery;
} => {
  if (!query) return {};
  const [{ value: searchContext }, subquery] = query.rules as [
    { value: SearchContext },
    QueryBuilderSubquery,
  ];
  return { searchContext, subquery };
};

export const QueryBuilder = ({
  query,
  defaultSearchContext = SearchContext.COMPONENT,
  asyncSuggestionsLoaders,
  asyncLabelLoaders,
  onQueryChange,
  onChange,
  showErrors,
  includeContextSelect,
  hideContainerStyles,
  hideTopLevelConditionBar,
  maxSubqueryNestingLevel = 2,
  allowedSearchContexts,
  isReadOnly,
  isDisabled,
  contextSelectIsReadOnly,
  getFilterGroups,
  clickIdPrefix,
  topLevelLabelContent,
  dataTestId,
  holdInvalidRules,
  getRuleNotificationParams,
  isCompact,
  isLoading,
  aiGenerateQueryFeedback,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  menuPortalTarget,
}: QueryBuilderProps) => {
  const isMountedRef = useRef(false);
  const { searchContext: searchContextFromQuery, subquery } =
    extractSearchContextAndSubqueryFromQuery(query);
  // more important is searchContext stored on input query, but if
  // its not defined there (eg. when input query is null) then we
  // relay on the required prop searchContext
  const initalSearchContext = searchContextFromQuery || defaultSearchContext;
  const initialFilterGroups = useMemo(
    () => getFilterGroups({ searchContext: initalSearchContext }),
    [initalSearchContext, getFilterGroups]
  );
  const { queryBuilderState, reducerActions } = useQueryBuilderReducer(
    subquery,
    initalSearchContext,
    initialFilterGroups,
    maxSubqueryNestingLevel
  );
  const { searchContext } = queryBuilderState;
  const filterGroups = useMemo(
    () => getFilterGroups({ searchContext }),
    [searchContext, getFilterGroups]
  );
  const prevQueryBuilderState = usePrevious(queryBuilderState);
  const previousFilterGroups = usePrevious(filterGroups);
  useEffect(() => {
    const query = reducerStateToQuery(
      queryBuilderState,
      filterGroups,
      holdInvalidRules
    );
    const isValid = getIsReducerStateValid(queryBuilderState);
    const isEmpty = getIsEmpty(query);

    const hasStateChanged =
      !isEqual(queryBuilderState, prevQueryBuilderState) ||
      !isEqual(filterGroups, previousFilterGroups);
    /**
     * isMountedRef.current prevents on calling the callbacks on initial component load
     * Withotout that the component calls onChange callback before doing any action which
     * is not expected.
     */
    if (isMountedRef.current && hasStateChanged) {
      onQueryChange?.(query);
      onChange?.({
        isValid,
        isEmpty,
        query,
        subquery: query.rules[1],
      });
    }
    isMountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryBuilderState,
    filterGroups,
    searchContext,
    // onQueryChange,
    // onChange,
    // prevQueryBuilderState,
  ]);

  return (
    <DndContextProvider
      queryBuilderState={queryBuilderState}
      onDrop={({ draggedRowId, position, overRuleId }) => {
        reducerActions.moveRow({ draggedRowId, position, overRuleId });
      }}
    >
      <QueryBuilderWrapper
        // we pass down data-testid for React Testing Library selectors and data-test-id for e2e tests
        data-testid={dataTestId}
        data-test-id={dataTestId}
        $skipStyles={hideContainerStyles}
        $isCompact={isCompact}
      >
        <QueryRow
          level={0}
          isDisabled={isDisabled ?? false}
          hideTopLevelConditionBar={hideTopLevelConditionBar}
          maxSubqueryNestingLevel={maxSubqueryNestingLevel}
          includeContextSelect={includeContextSelect}
          contextSelectIsReadOnly={contextSelectIsReadOnly}
          filterGroups={filterGroups}
          query={reducerStateToInternalQuery(queryBuilderState)}
          reducerActions={reducerActions}
          asyncSuggestionsLoaders={asyncSuggestionsLoaders}
          asyncLabelLoaders={asyncLabelLoaders}
          searchContext={searchContext}
          showErrors={showErrors}
          allowedSearchContexts={allowedSearchContexts}
          isReadOnly={Boolean(isReadOnly)}
          clickIdPrefix={clickIdPrefix}
          topLevelLabelContent={topLevelLabelContent}
          getRuleNotificationParams={getRuleNotificationParams}
          isCompact={isCompact}
          isLoading={isLoading}
          locale={locale}
          i18nLocale={i18nLocale}
          menuPortalTarget={menuPortalTarget}
        />
        {aiGenerateQueryFeedback && (
          <AiFeedbackWithDisclaimer {...aiGenerateQueryFeedback} />
        )}
      </QueryBuilderWrapper>
    </DndContextProvider>
  );
};
